"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readTemperature } from '@/lib/tools/numeric-input'

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~10 sec",
    })
  }
  return out
}

const COMMON_TEMPS = [
  { f: 32, c: 0, label: "Freezing point" },
  { f: 40, c: 4, label: "Refrigerator (USDA max)" },
  { f: 0, c: -18, label: "Freezer (USDA target)" },
  { f: 145, c: 63, label: "Beef / pork (USDA min)" },
  { f: 160, c: 71, label: "Ground meat (USDA min)" },
  { f: 165, c: 74, label: "Poultry (USDA min)" },
  { f: 212, c: 100, label: "Boiling point" },
  { f: 325, c: 163, label: "Low oven" },
  { f: 350, c: 177, label: "Moderate oven" },
  { f: 375, c: 191, label: "Medium-high oven" },
  { f: 400, c: 204, label: "Hot oven" },
  { f: 425, c: 218, label: "Very hot oven" },
  { f: 450, c: 232, label: "Extremely hot oven" },
]

export function CookingTempConverterPageClient() {
  const meta = TOOL_METADATA["cooking-temperature-converter"]

  const [temperature, setTemperature] = useState("350")
  const [fromUnit, setFromUnit] = useState("Fahrenheit")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const temp = readTemperature(temperature, { min: -100, max: 600 })
    if (temp === null) return null
    let converted: number
    let toUnit: string
    if (fromUnit === "Fahrenheit") {
      converted = ((temp - 32) * 5) / 9
      toUnit = "Celsius"
    } else {
      converted = (temp * 9) / 5 + 32
      toUnit = "Fahrenheit"
    }
    return { converted: parseFloat(converted.toFixed(1)), toUnit, fromTemp: temp }
  }, [temperature, fromUnit])

  return (
    <ToolPageShell
      toolName={meta.name}
      heroEyebrow={meta.category}
      heroTitlePre="Fahrenheit and Celsius for"
      heroTitleItalic="any"
      heroTitlePost="cooking step."
      heroSubtitle={meta.mediumDescription}
      heroMeta={["⏱ ~5 sec", "USDA safe-handling temps"]}
      heroShare={<ToolShareBar title={meta.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40°F", label: "Fridge max", note: "USDA" },
            { value: "0°F", label: "Freezer target", note: "USDA" },
            { value: "165°F", label: "Poultry min", note: "USDA" },
            { value: "350°F", label: "Moderate oven", note: "Baseline" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={meta.faqs.slice(0, 6).map((f) => ({ question: f.question, answer: f.answer }))}
      relatedTools={buildRelated(meta.relatedTools, "cooking-temperature-converter")}
      cta={{
        title: "Stable cold storage matters as much as oven temp.",
        body: "Browse refrigerators with precision digital controls that hold the USDA-recommended 40°F fridge / 0°F freezer targets without drifting.",
        buttonLabel: "Browse precision fridges",
        buttonHref: "/collections/french-door-refrigerators",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Type the number.
                <br />
                Pick the scale.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                For fan / convection ovens, subtract 25°F (or 14°C) from any conventional recipe temp.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Temperature
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      °{fromUnit === "Fahrenheit" ? "F" : "C"}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Convert from
                  </label>
                  <select
                    value={fromUnit}
                    onChange={(e) => setFromUnit(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="Fahrenheit">Fahrenheit (°F)</option>
                    <option value="Celsius">Celsius (°C)</option>
                  </select>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Formula: (°F − 32) × 5/9 = °C
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Converted value
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {result?.converted ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      °{result?.toUnit === "Fahrenheit" ? "F" : "C"}
                    </span>
                  </div>
                  <div className="mt-4 text-[15px] text-white/85 leading-[1.5]">
                    {result
                      ? `${result.fromTemp}°${fromUnit === "Fahrenheit" ? "F" : "C"} → ${result.converted}°${result.toUnit === "Fahrenheit" ? "F" : "C"}`
                      : "Enter any cooking, oven, or fridge/freezer temperature."}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Common temperatures table */}
          <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                Critical food-safety temperatures
              </h3>
              <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                USDA + standard oven
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {COMMON_TEMPS.map((t) => (
                <div key={t.label} className="flex items-baseline justify-between border-b border-gray-100 pb-1.5">
                  <span className="text-[13px] text-gray-700">{t.label}</span>
                  <span className="text-[13px] font-mono font-bold text-gray-900">
                    {t.f}°F · {t.c}°C
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Hold the temp"
          title="French door fridges to compare"
          description="Keep any fridge at 40°F or below, the USDA limit for raw meat and dairy; 37°F leaves a margin."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Pure formula. <em className="italic text-[#ff9951]">USDA</em> reference temps.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Conversion uses the international standard formula. Reference safe-handling temperatures come from USDA Food Safety and Inspection Service guidelines.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Fahrenheit → Celsius",
                  d: "(°F − 32) × 5/9. The freezing point of water sits at 32°F / 0°C; boiling at 212°F / 100°C.",
                },
                {
                  n: "02",
                  t: "USDA cold-storage targets",
                  d: "Refrigerator below 40°F (4°C). Freezer at 0°F (−18°C). These prevent bacterial growth on perishable foods.",
                },
                {
                  n: "03",
                  t: "Convection adjustment",
                  d: "Fan ovens cook ~25% faster — subtract 25°F (14°C) from any conventional recipe to compensate.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer fridges for fresh-food cooks"
          description="Eye-level fresh storage and consistent temperature zones — ideal for serious home cooks."
          collectionType="bottom-freezer"
          fallbackCollectionType="french-door"
        />
      </section>
    </ToolPageShell>
  )
}
